/**
 * Auto-apply discount code utility
 * 
 * This module automatically applies a discount code when products are added to the cart.
 * It listens for cart add events and applies the discount code from metaobjects.
 */

import { ThemeEvents, CartAddEvent, DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/**
 * Auto-apply discount code manager
 */
class AutoApplyDiscount {
  /** @type {string | null} */
  #discountCode = null;
  #isApplying = false;
  #applied = false;
  /** @type {MutationObserver | null} */
  #cartReadyObserver = null;
  /** @type {Promise<void> | null} */
  #activeApplyPromise = null;
  #boundHandleCartUpdate = this.#handleCartUpdate.bind(this);

  /**
   * Initialize the auto-apply discount functionality
   * @param {string} discountCode - The discount code to apply
   */
  init(discountCode) {
    if (!discountCode) {
      return;
    }

    this.#discountCode = discountCode;

    // Listen for cart add events
    document.addEventListener(ThemeEvents.cartUpdate, this.#boundHandleCartUpdate);
    this.#startCartObserver();
    
    // Also try to apply on page load if cart already has items
    this.#applyDiscountOnLoad();
  }

  /**
   * Handle cart update events (including cart add)
   */
  /**
   * @param {Event} event
   */
  async #handleCartUpdate(event) {
    // Only apply if it's a cart add event (not just any cart update) and we haven't applied yet
    if (event instanceof CartAddEvent && !event.detail?.data?.didError && !this.#applied && !this.#isApplying) {
      this.#setHeaderCartLoading(true);
      try {
        await this.#applyDiscountOnLoad();
      } finally {
        this.#setHeaderCartLoading(false);
        document.dispatchEvent(new CustomEvent('auto-discount:complete'));
      }
    }
  }

  /**
   * Apply discount code on page load if cart has items
   */
  async #applyDiscountOnLoad() {
    if (this.#applied || this.#isApplying) {
      return;
    }

    try {
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      if (cartData && cartData.item_count > 0) {
        // Check if discount is already applied
        const hasDiscount = this.#isDiscountApplied(cartData);

        if (!hasDiscount) {
          if (this.#shouldRedirectToDiscountUrl()) {
            this.#setRedirectGuard();
            this.#redirectToDiscountUrl();
            return;
          }
          await this.#applyDiscount();
        } else {
          if (this.#activeApplyPromise) {
            await this.#activeApplyPromise;
          }
          this.#applied = true;
          this.#clearRedirectGuard();
          this.#stopCartObserver();
        }
      }
    } catch (error) {
      console.debug('Failed to check cart on load:', error);
    }
  }

  /**
   * Apply the discount code to the cart
   */
  async #applyDiscount() {
    const discountCode = this.#discountCode;
    if (!discountCode || this.#applied) {
      return;
    }

    if (this.#activeApplyPromise) {
      await this.#activeApplyPromise;
      return;
    }

    this.#isApplying = true;
    this.#activeApplyPromise = (async () => {
      try {
        // First, get current cart to check existing discounts
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();

        if (!cartData || cartData.item_count === 0) {
          return;
        }

        // Check if discount is already applied
        const existingDiscounts = this.#getExistingDiscountCodes(cartData);

        if (existingDiscounts.includes(discountCode)) {
          this.#applied = true;
          this.#clearRedirectGuard();
          this.#stopCartObserver();
          return;
        }

        // Get all section IDs that need to be updated
        /** @type {string[]} */
        const sectionIds = [];
        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        cartItemsComponents.forEach((item) => {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            sectionIds.push(item.dataset.sectionId);
          }
        });

        const cartDiscountComponents = document.querySelectorAll('cart-discount-component');
        cartDiscountComponents.forEach((component) => {
          if (component instanceof HTMLElement && component.dataset.sectionId) {
            sectionIds.push(component.dataset.sectionId);
          }
        });

        // Apply the discount code
        const config = fetchConfig('json', {
          body: JSON.stringify({
            discount: [...existingDiscounts, discountCode].join(','),
            sections: sectionIds.length > 0 ? sectionIds.join(',') : undefined,
          }),
        });

        const response = await fetch('/cart/update.js', {
          ...config,
          method: 'POST',
        });

        if (!response.ok) {
          throw new Error('Failed to apply discount code');
        }

        const data = await response.json();

        // Check if discount was successfully applied
        const discountApplied = this.#isDiscountApplied(data);

        if (discountApplied) {
          this.#applied = true;
          this.#clearRedirectGuard();
          this.#stopCartObserver();
          
          // Dispatch discount update event to refresh cart UI
          document.dispatchEvent(new DiscountUpdateEvent(data, 'auto-apply-discount'));

          // Update cart sections using morphSection
          if (sectionIds.length > 0 && data.sections) {
            sectionIds.forEach((sectionId) => {
              const sectionHtml = data.sections[sectionId];
              if (sectionHtml) {
                morphSection(sectionId, sectionHtml);
              }
            });
          }
        }
      } catch (error) {
        console.debug('Auto-apply discount failed:', error);
      } finally {
        this.#isApplying = false;
        this.#activeApplyPromise = null;
      }
    })();

    await this.#activeApplyPromise;
  }

  #startCartObserver() {
    if (this.#cartReadyObserver) return;
    const observerTarget = document.body || document.documentElement;
    if (!observerTarget) return;

    this.#cartReadyObserver = new MutationObserver(() => {
      if (this.#applied || this.#isApplying) return;
      this.#applyDiscountOnLoad();
    });

    this.#cartReadyObserver.observe(observerTarget, {
      childList: true,
      subtree: true,
    });
  }

  #stopCartObserver() {
    this.#cartReadyObserver?.disconnect();
    this.#cartReadyObserver = null;
  }

  /**
   * @returns {boolean}
   */
  #isCartPage() {
    return /^\/cart(?:$|\/|\?)/.test(window.location.pathname + window.location.search);
  }

  /**
   * @returns {string}
   */
  #redirectGuardKey() {
    return `auto-apply-discount-redirected:${this.#normalizeCode(this.#discountCode)}`;
  }

  /**
   * @returns {boolean}
   */
  #hasRedirectGuard() {
    try {
      return sessionStorage.getItem(this.#redirectGuardKey()) === '1';
    } catch (_error) {
      return false;
    }
  }

  #setRedirectGuard() {
    try {
      sessionStorage.setItem(this.#redirectGuardKey(), '1');
    } catch (_error) {}
  }

  #clearRedirectGuard() {
    try {
      sessionStorage.removeItem(this.#redirectGuardKey());
    } catch (_error) {}
  }

  /**
   * @returns {boolean}
   */
  #shouldRedirectToDiscountUrl() {
    return this.#isCartPage() && !this.#hasRedirectGuard();
  }

  #redirectToDiscountUrl() {
    if (!this.#discountCode) return;
    const redirectUrl = '/cart';
    const encodedCode = encodeURIComponent(this.#discountCode);
    const encodedRedirect = encodeURIComponent(redirectUrl);
    window.location.assign(`/discount/${encodedCode}?redirect=${encodedRedirect}`);
  }

  /**
   * @param {boolean} isLoading
   */
  #setHeaderCartLoading(isLoading) {
    setHeaderCartLoading(isLoading);
  }

  /**
   * @param {string | null | undefined} code
   * @returns {string}
   */
  #normalizeCode(code) {
    return (code || '').trim().toLowerCase();
  }

  /**
   * @param {any} cartData
   * @returns {string[]}
   */
  #getExistingDiscountCodes(cartData) {
    const codesFromDiscountCodes = (cartData?.discount_codes || [])
      .filter(
        /** @param {{ code?: string; applicable?: boolean }} discount */
        (discount) => discount?.code && discount.applicable !== false
      )
      .map(
        /** @param {{ code?: string }} discount */
        (discount) => discount.code
      );

    const codesFromApplications = (cartData?.cart_level_discount_applications || [])
      .filter(
        /** @param {{ type?: string; title?: string }} discount */
        (discount) => discount?.type === 'discount_code' && discount?.title
      )
      .map(
        /** @param {{ title?: string }} discount */
        (discount) => discount.title
      );

    return [...new Set([...codesFromDiscountCodes, ...codesFromApplications].filter(Boolean))];
  }

  /**
   * @param {any} cartData
   * @returns {boolean}
   */
  #isDiscountApplied(cartData) {
    const targetCode = this.#normalizeCode(this.#discountCode);
    if (!targetCode) return false;

    return this.#getExistingDiscountCodes(cartData).some((code) => {
      return this.#normalizeCode(code) === targetCode;
    });
  }
}

// Create singleton instance
const autoApplyDiscount = new AutoApplyDiscount();

const discountCodeMeta = document.querySelector('meta[name="auto-apply-discount-code"]');
const discountCode = discountCodeMeta?.getAttribute('content');

if (discountCode) {
  autoApplyDiscount.init(discountCode);
}

/**
 * Blocks header cart icon clicks while cart/discount is updating.
 * @param {boolean} isLoading
 */
export function setHeaderCartLoading(isLoading) {
  document.documentElement.classList.toggle('auto-discount-cart-icon-loading', isLoading);
  const cartActions = document.querySelectorAll('.header-actions__action.action__cart');
  cartActions.forEach((action) => {
    if (!(action instanceof HTMLElement)) return;
    action.classList.toggle('is-cart-updating', isLoading);
    action.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
  });
}

export default autoApplyDiscount;

